import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiGift, FiCopy, FiCheck } from 'react-icons/fi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-blue-100 text-blue-700',
  redeemed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

export default function GiftsTab({ getAuthHeader }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('paid'); // default to "needs card sent"
  const [copiedId, setCopiedId] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/gifts`, { headers: getAuthHeader() });
      setItems(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filtered = filter === 'all' ? items : items.filter((g) => g.status === filter);

  const copyDetails = (g) => {
    const txt = [
      `Gift card details — The Hyderabad Library`,
      `From: ${g.anonymous ? 'A secret admirer' : (g.giver_name || g.giver_email)}`,
      `To: ${g.recipient_name || g.recipient_email}`,
      `Recipient email: ${g.recipient_email}`,
      `Plan: ${g.plan === 'annual' ? '1-year' : '1-month'} membership`,
      `Amount: ₹${((g.amount_paise || 0) / 100).toLocaleString('en-IN')}`,
      g.personal_message ? `Message: "${g.personal_message}"` : null,
      `Purchased: ${new Date(g.created_at).toLocaleDateString()}`,
      `Reference: ${g.id}`,
    ].filter(Boolean).join('\n');
    navigator.clipboard.writeText(txt).then(() => {
      setCopiedId(g.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const counts = {
    paid: items.filter((g) => g.status === 'paid').length,
    redeemed: items.filter((g) => g.status === 'redeemed').length,
    pending: items.filter((g) => g.status === 'pending').length,
    failed: items.filter((g) => g.status === 'failed').length,
  };

  return (
    <div className="space-y-4" data-testid="gifts-tab">
      <div className="flex items-center gap-3">
        <FiGift className="w-6 h-6 text-rose-600" />
        <h2 className="text-2xl font-bold text-gray-800">Gift Cards</h2>
      </div>
      <p className="text-sm text-gray-600">
        Members who&apos;ve purchased a membership gift. Use the <strong>Copy details</strong> button to grab the giver+recipient info,
        then send the card creative to the giver manually (WhatsApp / email).
      </p>

      <div className="flex flex-wrap gap-2">
        <FilterChip label={`Paid — needs card sent (${counts.paid})`} active={filter === 'paid'} onClick={() => setFilter('paid')} testId="gifts-filter-paid" />
        <FilterChip label={`Redeemed (${counts.redeemed})`} active={filter === 'redeemed'} onClick={() => setFilter('redeemed')} testId="gifts-filter-redeemed" />
        <FilterChip label={`Pending (${counts.pending})`} active={filter === 'pending'} onClick={() => setFilter('pending')} testId="gifts-filter-pending" />
        <FilterChip label={`Failed (${counts.failed})`} active={filter === 'failed'} onClick={() => setFilter('failed')} testId="gifts-filter-failed" />
        <FilterChip label={`All (${items.length})`} active={filter === 'all'} onClick={() => setFilter('all')} testId="gifts-filter-all" />
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
          No {filter === 'all' ? 'gift cards' : filter + ' gift cards'} yet.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-3 px-4">From (Giver)</th>
                <th className="py-3 px-4">To (Recipient)</th>
                <th className="py-3 px-4">Plan</th>
                <th className="py-3 px-4">Message</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g) => (
                <tr key={g.id} className="border-t hover:bg-gray-50" data-testid={`gift-row-${g.id}`}>
                  <td className="py-3 px-4 align-top">
                    <p className="font-medium text-gray-800">
                      {g.anonymous ? <span className="italic text-gray-500">Anonymous</span> : g.giver_name}
                    </p>
                    <p className="text-xs text-gray-500">{g.giver_email}</p>
                    <p className="text-xs text-gray-400 mt-1">{new Date(g.created_at).toLocaleDateString()}</p>
                  </td>
                  <td className="py-3 px-4 align-top">
                    <p className="font-medium text-gray-800">{g.recipient_name || <span className="italic text-gray-400">(no name)</span>}</p>
                    <p className="text-xs text-gray-500">{g.recipient_email}</p>
                  </td>
                  <td className="py-3 px-4 align-top">
                    <span className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-semibold">
                      {g.plan === 'annual' ? 'Annual' : 'Monthly'}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">₹{((g.amount_paise || 0) / 100).toLocaleString('en-IN')}</p>
                  </td>
                  <td className="py-3 px-4 align-top max-w-xs">
                    {g.personal_message ? (
                      <p className="italic text-gray-700 text-xs">&quot;{g.personal_message}&quot;</p>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-3 px-4 align-top">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_BADGE[g.status] || 'bg-gray-100 text-gray-700'}`}>
                      {g.status}
                    </span>
                    {g.redeemed_at && (
                      <p className="text-xs text-gray-400 mt-1">on {new Date(g.redeemed_at).toLocaleDateString()}</p>
                    )}
                  </td>
                  <td className="py-3 px-4 align-top text-right">
                    <button
                      onClick={() => copyDetails(g)}
                      className="px-3 py-1.5 text-xs bg-rose-600 hover:bg-rose-700 text-white rounded flex items-center gap-1 ml-auto"
                      data-testid={`copy-gift-${g.id}`}
                    >
                      {copiedId === g.id ? <><FiCheck className="w-3 h-3" /> Copied</> : <><FiCopy className="w-3 h-3" /> Copy details</>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FilterChip({ label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
        active ? 'bg-rose-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
      data-testid={testId}
    >{label}</button>
  );
}
